/**
 * ServerTimeContext.js
 * Contexto para sincronizar la hora del servidor con el cliente
 * Esto previene que usuarios manipulen la hora local del PC para saltarse validaciones
 */
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import { message, Modal } from "antd";
import dayjs from "./utils/dayjs";
import api from "./axiosConfig";

const ServerTimeContext = createContext();

// Umbral de desfase permitido en minutos
const MAX_TIME_DRIFT_MINUTES = 5;

// Intervalo de sincronización en milisegundos (cada 5 minutos)
const SYNC_INTERVAL_MS = 5 * 60 * 1000;

export const useServerTime = () => {
  const context = useContext(ServerTimeContext);
  if (!context) {
    throw new Error("useServerTime debe usarse dentro de ServerTimeProvider");
  }
  return context;
};

export const ServerTimeProvider = ({ children }) => {
  // Diferencia entre hora del servidor y hora local (en milisegundos)
  const [timeDrift, setTimeDrift] = useState(0);
  // Indica si la hora está sincronizada
  const [isSynced, setIsSynced] = useState(false);
  // Indica si hay un desfase excesivo
  const [hasExcessiveDrift, setHasExcessiveDrift] = useState(false);
  // Última sincronización
  const [lastSyncTime, setLastSyncTime] = useState(null);
  // Error de sincronización
  const [syncError, setSyncError] = useState(null);

  /**
   * Sincroniza la hora con el servidor
   */
  const syncTime = useCallback(async () => {
    try {
      const clientTimeBefore = Date.now();
      const response = await api.get("/server-time");
      const clientTimeAfter = Date.now();

      if (response.data?.success && response.data?.data) {
        const serverTimestamp = response.data.data.timestamp_ms;

        // Calcular el tiempo de latencia aproximado
        const roundTripTime = clientTimeAfter - clientTimeBefore;
        const latency = roundTripTime / 2;

        // Calcular la diferencia ajustada por latencia
        const clientTimeAtResponse = clientTimeBefore + latency;
        const drift = serverTimestamp - clientTimeAtResponse;

        setTimeDrift(drift);
        setIsSynced(true);
        setLastSyncTime(new Date());
        setSyncError(null);

        // Verificar si el desfase es excesivo
        const driftMinutes = Math.abs(drift) / 1000 / 60;
        const excessive = driftMinutes > MAX_TIME_DRIFT_MINUTES;
        setHasExcessiveDrift(excessive);

        if (excessive) {
          console.warn(
            `[ServerTime] Desfase excesivo detectado: ${driftMinutes.toFixed(1)} minutos`
          );
        }

        return { success: true, drift, driftMinutes };
      }

      throw new Error("Respuesta inválida del servidor");
    } catch (error) {
      console.error("[ServerTime] Error al sincronizar hora:", error);
      setSyncError(error.message);
      // En caso de error, asumimos que no hay desfase
      setTimeDrift(0);
      setIsSynced(false);
      return { success: false, error: error.message };
    }
  }, []);

  /**
   * Obtiene la hora actual del servidor (ajustada)
   * @returns {dayjs.Dayjs} Hora actual según el servidor
   */
  const getServerTime = useCallback(() => {
    const adjustedTime = Date.now() + timeDrift;
    return dayjs(adjustedTime);
  }, [timeDrift]);

  /**
   * Obtiene la hora actual del servidor como timestamp
   * @returns {number} Timestamp en milisegundos
   */
  const getServerTimestamp = useCallback(() => {
    return Date.now() + timeDrift;
  }, [timeDrift]);

  /**
   * Verifica si una hora está en el pasado según el servidor
   * @param {dayjs.Dayjs} time - Hora a verificar
   * @returns {boolean} True si está en el pasado
   */
  const isInPast = useCallback(
    (time) => {
      if (!time) return false;
      const serverNow = getServerTime();
      return dayjs(time).isBefore(serverNow);
    },
    [getServerTime]
  );

  /**
   * Verifica si una fecha es hoy según el servidor
   * @param {dayjs.Dayjs} date - Fecha a verificar
   * @returns {boolean} True si es hoy
   */
  const isToday = useCallback(
    (date) => {
      if (!date) return false;
      const serverNow = getServerTime();
      return dayjs(date).isSame(serverNow, "day");
    },
    [getServerTime]
  );

  /**
   * Muestra advertencia de desfase de hora
   */
  const showDriftWarning = useCallback(() => {
    const driftMinutes = Math.abs(timeDrift) / 1000 / 60;
    Modal.warning({
      title: "Advertencia: Hora del sistema incorrecta",
      content: (
        <div>
          <p>
            Se ha detectado que la hora de tu computador tiene un desfase de{" "}
            <strong>{driftMinutes.toFixed(0)} minutos</strong> con respecto al
            servidor.
          </p>
          <p>
            Por favor, sincroniza la hora de tu sistema operativo para evitar
            problemas con las validaciones de horarios.
          </p>
          <p style={{ marginTop: 16, color: "#666" }}>
            <small>
              Hora del servidor: {getServerTime().format("DD/MM/YYYY HH:mm:ss")}
              <br />
              Hora de tu PC: {dayjs().format("DD/MM/YYYY HH:mm:ss")}
            </small>
          </p>
        </div>
      ),
      okText: "Entendido",
    });
  }, [timeDrift, getServerTime]);

  // Sincronización inicial y periódica
  useEffect(() => {
    // Sincronización inicial
    syncTime();

    // Sincronización periódica
    const intervalId = setInterval(syncTime, SYNC_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [syncTime]);

  // Mostrar advertencia cuando hay desfase excesivo
  useEffect(() => {
    if (hasExcessiveDrift && isSynced) {
      // Mostrar mensaje de advertencia
      message.warning(
        "Se detectó que la hora de tu computador está desconfigurada",
        5
      );
    }
  }, [hasExcessiveDrift, isSynced]);

  const contextValue = useMemo(
    () => ({
      // Estado
      timeDrift,
      isSynced,
      hasExcessiveDrift,
      lastSyncTime,
      syncError,

      // Funciones
      syncTime,
      getServerTime,
      getServerTimestamp,
      isInPast,
      isToday,
      showDriftWarning,

      // Constantes
      MAX_TIME_DRIFT_MINUTES,
    }),
    [
      timeDrift,
      isSynced,
      hasExcessiveDrift,
      lastSyncTime,
      syncError,
      syncTime,
      getServerTime,
      getServerTimestamp,
      isInPast,
      isToday,
      showDriftWarning,
    ]
  );

  return (
    <ServerTimeContext.Provider value={contextValue}>
      {children}
    </ServerTimeContext.Provider>
  );
};

ServerTimeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ServerTimeContext;
